// SignalLens icon set — subset needed by the web shell. Pure SVG components,
// styled via CSS variables from styles.css so light/dark theme switch is
// automatic.
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

export const PlayIcon = (props: IconProps) => (
	<svg viewBox="0 0 24 24" fill="currentColor" {...props}>
		<path d="M8 5.5v13l11-6.5Z" />
	</svg>
);

export const StopIcon = (props: IconProps) => (
	<svg viewBox="0 0 24 24" fill="currentColor" {...props}>
		<rect x="6" y="6" width="12" height="12" rx="2" />
	</svg>
);

export const RefreshIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<path d="M20 12a8 8 0 1 1-2.34-5.66" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M20 4v4h-4" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const CopyIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<rect x="8" y="8" width="12" height="12" rx="2.5" />
		<path
			d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-8A1.5 1.5 0 0 0 5 5.5v8A1.5 1.5 0 0 0 6.5 15H8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export const CloseIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		{...props}
	>
		<path d="M6 6l12 12" />
		<path d="M18 6L6 18" />
	</svg>
);

export const PlusIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		{...props}
	>
		<path d="M12 5v14" />
		<path d="M5 12h14" />
	</svg>
);

export const TrashIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<path d="M4.5 7.5h15" strokeLinecap="round" />
		<path
			d="M9.5 4.75h5a1 1 0 0 1 1 1V7.5h-7V5.75a1 1 0 0 1 1-1Z"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M6.5 7.5l.9 11a1.75 1.75 0 0 0 1.74 1.6h5.72a1.75 1.75 0 0 0 1.74-1.6l.9-11"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export const PencilIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<path
			d="m4 20 1-4 11-11 3 3-11 11-4 1Z"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="m13.5 6.5 3 3" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const SignalLensLogo = (props: IconProps) => (
	<svg viewBox="0 0 64 64" fill="none" aria-hidden="true" {...props}>
		<rect
			x="2"
			y="2"
			width="60"
			height="60"
			rx="20"
			fill="var(--color-tg-logo-panel)"
		/>
		<rect
			x="2"
			y="2"
			width="60"
			height="60"
			rx="20"
			stroke="var(--color-tg-logo-stroke)"
		/>
		<circle
			cx="32"
			cy="32"
			r="16"
			stroke="var(--color-tg-logo-ring)"
			strokeWidth="3.5"
		/>
		<circle cx="32" cy="32" r="6" fill="var(--color-tg-logo-core)" />
		<path
			d="M14 42h9l5-7 6 4 11-12 5 4"
			stroke="var(--color-tg-logo-route)"
			strokeWidth="3"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<circle cx="14" cy="42" r="2.2" fill="var(--color-tg-logo-route)" />
		<circle cx="28" cy="35" r="2.2" fill="var(--color-tg-logo-route)" />
		<circle cx="45" cy="27" r="2.2" fill="var(--color-tg-logo-route)" />
		<path
			d="M45 19v6M42 22h6"
			stroke="var(--color-tg-logo-plus)"
			strokeWidth="2"
			strokeLinecap="round"
		/>
	</svg>
);

export const SettingsIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<path
			d="M12 8.75A3.25 3.25 0 1 1 8.75 12 3.25 3.25 0 0 1 12 8.75Z"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M19.13 13.5a1.26 1.26 0 0 0 .25 1.39l.05.05a1.5 1.5 0 0 1 0 2.12l-.37.37a1.5 1.5 0 0 1-2.12 0l-.05-.05a1.26 1.26 0 0 0-1.39-.25 1.26 1.26 0 0 0-.76 1.15V19a1.5 1.5 0 0 1-1.5 1.5h-.52a1.5 1.5 0 0 1-1.5-1.5v-.07a1.26 1.26 0 0 0-.76-1.15 1.26 1.26 0 0 0-1.39.25l-.05.05a1.5 1.5 0 0 1-2.12 0l-.37-.37a1.5 1.5 0 0 1 0-2.12l.05-.05a1.26 1.26 0 0 0 .25-1.39 1.26 1.26 0 0 0-1.15-.76H5a1.5 1.5 0 0 1-1.5-1.5v-.52A1.5 1.5 0 0 1 5 9.82h.07a1.26 1.26 0 0 0 1.15-.76 1.26 1.26 0 0 0-.25-1.39l-.05-.05a1.5 1.5 0 0 1 0-2.12l.37-.37a1.5 1.5 0 0 1 2.12 0l.05.05a1.26 1.26 0 0 0 1.39.25 1.26 1.26 0 0 0 .76-1.15V5a1.5 1.5 0 0 1 1.5-1.5h.52a1.5 1.5 0 0 1 1.5 1.5v.07a1.26 1.26 0 0 0 .76 1.15 1.26 1.26 0 0 0 1.39-.25l.05-.05a1.5 1.5 0 0 1 2.12 0l.37.37a1.5 1.5 0 0 1 0 2.12l-.05.05a1.26 1.26 0 0 0-.25 1.39 1.26 1.26 0 0 0 1.15.76H19a1.5 1.5 0 0 1 1.5 1.5v.52A1.5 1.5 0 0 1 19 13.5Z"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export const SunIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<circle cx="12" cy="12" r="4.25" />
		<path d="M12 2.75v2.5" strokeLinecap="round" />
		<path d="M12 18.75v2.5" strokeLinecap="round" />
		<path d="m5.46 5.46 1.77 1.77" strokeLinecap="round" />
		<path d="m16.77 16.77 1.77 1.77" strokeLinecap="round" />
		<path d="M2.75 12h2.5" strokeLinecap="round" />
		<path d="M18.75 12h2.5" strokeLinecap="round" />
		<path d="m5.46 18.54 1.77-1.77" strokeLinecap="round" />
		<path d="m16.77 7.23 1.77-1.77" strokeLinecap="round" />
	</svg>
);

export const MoonIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<path
			d="M18.5 14.1A6.9 6.9 0 0 1 9.9 5.5a7.75 7.75 0 1 0 8.6 8.6Z"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export const LogoutIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<path
			d="M10 19H5.5A1.5 1.5 0 0 1 4 17.5v-11A1.5 1.5 0 0 1 5.5 5H10"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="M15 15l4-3-4-3" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M19 12H9.5" strokeLinecap="round" />
	</svg>
);

export const UsersIcon = (props: IconProps) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		{...props}
	>
		<circle cx="9" cy="8" r="3.2" strokeLinecap="round" />
		<path
			d="M3.5 19c.5-3 3-4.6 5.5-4.6S14 16 14.5 19"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<circle cx="16.5" cy="9" r="2.4" strokeLinecap="round" />
		<path
			d="M14 14.6c1.6.2 4 1.4 4.5 4.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
